"use client";

import {
  RiRainbowLine,     // Rainbow for main festival
  RiMoonLine,        // Moon for after-party
  RiSunLine,         // Sun for pre-party
  RiCheckLine
} from "@remixicon/react";
import type { FestivalDetails, FestivalEventType } from "@workspace/types";
import { Button } from "@workspace/ui/components/button";
import {
  Command,
  CommandGroup,
  CommandItem,
} from "@workspace/ui/components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { useState, useEffect, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { isShowListEventGroup, getEventTypeLabel } from "@workspace/shared-utils";

// Event group configuration
const ALL_EVENT_GROUPS = [
  {
    id: 'main_festival',
    name: 'Festival',
    type: 'integrated' as const, // Calendar/Time/Stages view
    icon: RiRainbowLine,
  },
  {
    id: 'pre_party',
    name: 'Pre-Party',
    type: 'integrated' as const, // Calendar/Time/Stages view (same-venue support)
    icon: RiSunLine,
  },
  {
    id: 'after_party',
    name: 'Official Afterparty',
    type: 'integrated' as const, // Calendar/Time/Stages view (same-venue support)
    icon: RiMoonLine,
  },
  {
    id: 'recovery_party',
    name: 'Recovery Party',
    type: 'show_list' as const, // Show cards view (different-venue shows)
    icon: RiMoonLine,
  },
] as const;

type EventGroupType = 'integrated' | 'show_list';

interface EventGroup {
  id: string;
  name: string;
  type: EventGroupType;
  icon: typeof RiRainbowLine;
  event_type?: string; // Original event_type for routing (optional for backward compat)
}

interface EventGroupSelectorProps {
  className?: string;
  onGroupChange?: (groupId: string, viewType: EventGroupType) => void;
  availableEventTypes?: FestivalEventType[] | undefined; // Available event types with custom names (undefined = loading)
  festivalDetails?: FestivalDetails; // Festival details for custom labels
}

export function EventGroupSelector({
  className,
  onGroupChange,
  availableEventTypes,
  festivalDetails,
}: EventGroupSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);

  // Build event groups from API data with custom names already resolved
  const EVENT_GROUPS = useMemo(() => {
    // Validate data format - detect old cached format (array of strings) vs new format (array of objects)
    const isValidFormat = availableEventTypes?.every(
      et => et && typeof et === 'object' && 'event_type' in et && 'event_name' in et
    );

    // If still loading or invalid format (old cache), use the current URL param to determine what to show
    if (!availableEventTypes || !isValidFormat) {
      const eventGroupParam = searchParams?.get('eventGroup') ?? null;
      // If there's an eventGroup in URL, show main_festival plus that group
      if (eventGroupParam && eventGroupParam !== 'main_festival') {
        return ALL_EVENT_GROUPS
          .filter(g => g.id === 'main_festival' || g.id === eventGroupParam)
          .map(g => {
            // Apply custom labels even while loading
            const customLabel = getEventTypeLabel(g.id, festivalDetails);
            return { ...g, name: customLabel, event_type: g.id as "main_festival" | "pre_party" | "after_party" };
          });
      }
      // Default: just show main_festival while loading
      return ALL_EVENT_GROUPS.filter(g => g.id === 'main_festival').map(g => ({
        ...g,
        event_type: g.id as "main_festival" | "pre_party" | "after_party"
      }));
    }

    // Map API data to event groups with custom names from database
    // API returns unique event_id for each event (event_type for integrated, event_type_index for show_list)
    const groups = availableEventTypes.map((et) => {
      // Get icon from our icon mapping
      const iconMapping: Record<string, typeof RiRainbowLine> = {
        main_festival: RiRainbowLine,
        pre_party: RiSunLine,
        after_party: RiMoonLine,
      };

      // Use event_id directly from API (already unique)
      const id = et.event_id;

      return {
        id,
        name: et.event_name, // Use custom name from database
        type: et.view_type,
        icon: iconMapping[et.event_type] || RiRainbowLine,
        event_type: et.event_type, // Store original event_type for routing
      };
    });

    return groups;
  }, [availableEventTypes, festivalDetails, searchParams]);

  // Get initial group from URL or default to main_festival
  const eventGroupParam = searchParams?.get('eventGroup') ?? null;
  // Match by id (supports both event_type strings and UUIDs for show_list events)
  const initialGroup = EVENT_GROUPS.find(g => g.id === eventGroupParam) || EVENT_GROUPS[0];
  const [selectedGroup, setSelectedGroup] = useState(initialGroup);

  // Validate data format
  const isValidFormat = availableEventTypes?.every(
    et => et && typeof et === 'object' && 'event_type' in et && 'event_name' in et
  );

  // Hide selector only if data has loaded with valid format AND there's only one or zero event types
  // Show selector while loading or if there are multiple event types
  if (availableEventTypes && isValidFormat) {
    // Data has loaded with valid format, check actual count
    if (availableEventTypes.length <= 1) {
      return null;
    }
  }

  // Update selected group when URL changes
  useEffect(() => {
    const eventGroupParam = searchParams?.get('eventGroup') ?? null;
    // Match by id (supports both event_type strings and UUIDs for show_list events)
    const groupFromUrl = EVENT_GROUPS.find(g => g.id === eventGroupParam);
    if (groupFromUrl && selectedGroup && groupFromUrl.id !== selectedGroup.id) {
      setSelectedGroup(groupFromUrl);
      onGroupChange?.(groupFromUrl.event_type || groupFromUrl.id, groupFromUrl.type);
    }
  }, [searchParams, selectedGroup?.id, onGroupChange, EVENT_GROUPS]);

  const handleSelect = useCallback((groupId: string) => {
    const group = EVENT_GROUPS.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      onGroupChange?.(group.event_type || group.id, group.type);
      setOpen(false);

      // Update URL - use unique id for show_list events (to handle multiple events of same type)
      // For integrated events, use event_type for cleaner URLs
      const params = new URLSearchParams(searchParams?.toString() || '');
      const urlParam = group.type === 'show_list' ? group.id : (group.event_type || group.id);
      if (urlParam === 'main_festival') {
        // Remove param for default
        params.delete('eventGroup');
      } else {
        params.set('eventGroup', urlParam);
      }

      const currentPath = pathname || '/';
      const newUrl = params.toString() ? `${currentPath}?${params.toString()}` : currentPath;
      router.push(newUrl, { scroll: false });
    }
  }, [searchParams, pathname, router, onGroupChange, EVENT_GROUPS]);

  // If no selected group, don't render (shouldn't happen but guards against undefined)
  if (!selectedGroup) {
    return null;
  }

  const SelectedIcon = selectedGroup.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          className={cn("inline-flex items-center text-sm", className)}
        >
          <SelectedIcon className="mr-1 h-4 w-4" />
          <span>{selectedGroup.name}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="end">
        <Command>
          <CommandGroup>
            {EVENT_GROUPS.map((group) => {
              const GroupIcon = group.icon;
              return (
                <CommandItem
                  key={group.id}
                  value={group.id}
                  onSelect={() => handleSelect(group.id)}
                >
                  <GroupIcon className="h-4 w-4" />
                  {group.name}
                  {group.id === selectedGroup.id && (
                    <RiCheckLine className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
